import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { LOGO_LIGHT } from '../App';
import { Phone, ChevronDown, Mail } from 'lucide-react';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import { APP_VERSION } from '../config/version';
import { trackLogin, trackSignUp } from '../lib/analytics';
import LanguageSelectorBadge from '../components/LanguageSelectorBadge';
import PasswordInput from '../components/PasswordInput';
import { isNative, browser } from '../lib/native';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const API = API_URL;

const SPLASH_IMAGE = '/sfondo_login.webp';
const DESKTOP_BG_IMAGE = '/sfondo_login.webp';

// Country codes with flags
const COUNTRIES = [
  { code: '+39', flag: '🇮🇹', name: 'Italia', iso: 'IT' },
  { code: '+1', flag: '🇺🇸', name: 'USA', iso: 'US' },
  { code: '+44', flag: '🇬🇧', name: 'UK', iso: 'GB' },
  { code: '+33', flag: '🇫🇷', name: 'France', iso: 'FR' },
  { code: '+49', flag: '🇩🇪', name: 'Deutschland', iso: 'DE' },
  { code: '+34', flag: '🇪🇸', name: 'España', iso: 'ES' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal', iso: 'PT' },
  { code: '+55', flag: '🇧🇷', name: 'Brasil', iso: 'BR' },
  { code: '+41', flag: '🇨🇭', name: 'Svizzera', iso: 'CH' },
  { code: '+43', flag: '🇦🇹', name: 'Österreich', iso: 'AT' },
  { code: '+31', flag: '🇳🇱', name: 'Nederland', iso: 'NL' },
  { code: '+32', flag: '🇧🇪', name: 'Belgique', iso: 'BE' },
  { code: '+46', flag: '🇸🇪', name: 'Sverige', iso: 'SE' },
  { code: '+47', flag: '🇳🇴', name: 'Norge', iso: 'NO' },
  { code: '+45', flag: '🇩🇰', name: 'Danmark', iso: 'DK' },
  { code: '+48', flag: '🇵🇱', name: 'Polska', iso: 'PL' },
  { code: '+30', flag: '🇬🇷', name: 'Ελλάδα', iso: 'GR' },
  { code: '+90', flag: '🇹🇷', name: 'Türkiye', iso: 'TR' },
  { code: '+7', flag: '🇷🇺', name: 'Россия', iso: 'RU' },
  { code: '+81', flag: '🇯🇵', name: '日本', iso: 'JP' },
  { code: '+86', flag: '🇨🇳', name: '中国', iso: 'CN' },
  { code: '+91', flag: '🇮🇳', name: 'India', iso: 'IN' },
  { code: '+61', flag: '🇦🇺', name: 'Australia', iso: 'AU' },
  { code: '+52', flag: '🇲🇽', name: 'México', iso: 'MX' },
  { code: '+54', flag: '🇦🇷', name: 'Argentina', iso: 'AR' },
  { code: '+56', flag: '🇨🇱', name: 'Chile', iso: 'CL' },
  { code: '+57', flag: '🇨🇴', name: 'Colombia', iso: 'CO' },
  { code: '+212', flag: '🇲🇦', name: 'المغرب', iso: 'MA' },
  { code: '+216', flag: '🇹🇳', name: 'تونس', iso: 'TN' },
  { code: '+20', flag: '🇪🇬', name: 'مصر', iso: 'EG' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria', iso: 'NG' },
  { code: '+27', flag: '🇿🇦', name: 'South Africa', iso: 'ZA' },
  { code: '+82', flag: '🇰🇷', name: '한국', iso: 'KR' },
  { code: '+66', flag: '🇹🇭', name: 'ไทย', iso: 'TH' },
  { code: '+62', flag: '🇮🇩', name: 'Indonesia', iso: 'ID' },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia', iso: 'MY' },
  { code: '+63', flag: '🇵🇭', name: 'Philippines', iso: 'PH' },
  { code: '+971', flag: '🇦🇪', name: 'UAE', iso: 'AE' },
  { code: '+966', flag: '🇸🇦', name: 'السعودية', iso: 'SA' },
  { code: '+972', flag: '🇮🇱', name: 'ישראל', iso: 'IL' },
  { code: '+40', flag: '🇷🇴', name: 'România', iso: 'RO' },
  { code: '+36', flag: '🇭🇺', name: 'Magyarország', iso: 'HU' },
  { code: '+420', flag: '🇨🇿', name: 'Česko', iso: 'CZ' },
  { code: '+380', flag: '🇺🇦', name: 'Україна', iso: 'UA' },
  { code: '+385', flag: '🇭🇷', name: 'Hrvatska', iso: 'HR' },
  { code: '+381', flag: '🇷🇸', name: 'Србија', iso: 'RS' },
  { code: '+355', flag: '🇦🇱', name: 'Shqipëria', iso: 'AL' },
];

function getDefaultCountry() {
  try {
    const lang = navigator.language || navigator.languages?.[0] || 'it';
    const region = lang.split('-')[1]?.toUpperCase() || lang.toUpperCase();
    const match = COUNTRIES.find(c => c.iso === region);
    if (match) return match;
    // Fallback by language code
    const langMap = { it: 'IT', en: 'US', fr: 'FR', de: 'DE', es: 'ES', pt: 'PT' };
    const langISO = langMap[lang.split('-')[0]];
    if (langISO) return COUNTRIES.find(c => c.iso === langISO) || COUNTRIES[0];
  } catch {}
  return COUNTRIES[0]; // Italy default
}

export default function PhoneLogin() {
  const { setUser } = useAuth();
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Flow steps:
  //  'login-choice'    — pick email or phone login (default landing)
  //  'login'           — phone + password
  //  'phone'           — enter phone number (for registration, triggered by "Registrati")
  //  'otp'             — enter SMS code (registration flow)
  //  'setup-password'  — set password after OTP verify (registration completion)
  //  'forgot-phone'    — enter phone number (for password reset)
  //  'forgot-otp'      — enter SMS code for reset
  //  'forgot-password' — set new password after OTP verify
  //
  // Honor `?mode=phone` so external links (e.g. EmailLogin's "Accedi
  // con telefono" fallback) can deep-link straight into the phone
  // form, skipping the choice screen.
  const initialStep = searchParams.get('mode') === 'phone' ? 'login' : 'login-choice';
  const [step, setStep] = useState(initialStep);
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);

  // Respect the admin "Google login" toggle. On native the OAuth return
  // is handled via the slimmy:// deep link (source=app).
  useEffect(() => {
    httpClient.get(`${API}/api/settings/public`)
      .then((res) => setGoogleLoginEnabled(!!res.data.google_login_enabled))
      .catch(() => setGoogleLoginEnabled(false));
  }, []);

  const handleGoogleLogin = async () => {
    try {
      trackLogin('google');
      if (isNative) {
        await browser.open(`${API}/api/auth/google/login?source=app`);
      } else {
        window.location.href = `${API}/api/auth/google/login`;
      }
    } catch (err) {
      console.error('[GoogleLogin] PhoneLogin failed:', err);
      setError(t('auth.googleLoginError') || 'Errore durante il login con Google. Riprova.');
    }
  };
  const [country, setCountry] = useState(() => getDefaultCountry());
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Hidden dev switch
  const [devTapCount, setDevTapCount] = useState(0);
  const [showDevSwitch, setShowDevSwitch] = useState(false);
  const [devPassword, setDevPassword] = useState('');
  const devTapTimer = useRef(null);

  const handleDevTap = () => {
    const newCount = devTapCount + 1;
    setDevTapCount(newCount);
    clearTimeout(devTapTimer.current);
    if (newCount >= 5) {
      setShowDevSwitch(true);
      setDevTapCount(0);
    } else {
      devTapTimer.current = setTimeout(() => setDevTapCount(0), 3000);
    }
  };

  const handleDevSwitch = () => {
    if (devPassword !== '07071974') return;
    const currentUrl = API_URL;
    const isPreview = currentUrl.includes('preview');
    const newUrl = isPreview ? 'https://slimmy.app' : currentUrl.replace('slimmy.app', 'slimmy.app'); // preview URL set dynamically
    
    // Clear session
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    
    // Set override and redirect
    if (isPreview) {
      sessionStorage.setItem('api_override', 'https://slimmy.app');
      window.location.href = 'https://slimmy.app/app/login';
    } else {
      sessionStorage.removeItem('api_override');
      // Go to preview
      const previewUrl = process.env.REACT_APP_BACKEND_URL || currentUrl;
      window.location.href = `${previewUrl}/app/login`;
    }
  };

  const otpRefs = [useRef(), useRef(), useRef(), useRef()];
  const phoneInputRef = useRef();

  // Phone-auth feature toggle. When admin disables it server-side, we
  // redirect immediately to email login so the user never sees a phone
  // form they can't actually use.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await httpClient.get(`${API}/api/settings/public`);
        if (cancelled) return;
        if (data?.phone_login_enabled === false) {
          navigate('/app/accedi', { replace: true });
        }
      } catch {
        /* fail-open: keep phone form visible if settings can't be fetched */
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const fullPhoneNumber = `${country.code}${phoneNumber.replace(/^0+/, '')}`;

  const isForgotFlow = () => step === 'forgot-phone' || step === 'forgot-otp' || step === 'forgot-password';

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    if (!phoneNumber.trim() || phoneNumber.length < 5) {
      setError('Inserisci un numero di telefono valido');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Forgot flow uses the same send-otp endpoint (OTP is sent regardless,
      // we just branch the next step)
      const endpoint = isForgotFlow() ? '/api/auth/phone/forgot-password' : '/api/auth/phone/send-otp';
      await httpClient.post(`${API}${endpoint}`, {
        phone_number: fullPhoneNumber,
        language: language || 'it'
      });
      setStep(isForgotFlow() ? 'forgot-otp' : 'otp');
      setResendTimer(60);
      setTimeout(() => otpRefs[0].current?.focus(), 300);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.data?.detail || 'Errore nell\'invio del codice';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    
    // iOS autofill sends all digits to the first input
    if (digits.length >= 4) {
      const code = digits.slice(0, 4);
      const newOtp = code.split('');
      setOtp(newOtp);
      otpRefs[3].current?.focus();
      setTimeout(() => handleVerifyOTP(code), 100);
      return;
    }
    
    // Multi-digit input (2-3 chars) — spread across boxes
    if (digits.length > 1) {
      const newOtp = [...otp];
      for (let i = 0; i < digits.length && index + i < 4; i++) {
        newOtp[index + i] = digits[i];
      }
      setOtp(newOtp);
      const nextIdx = Math.min(index + digits.length, 3);
      otpRefs[nextIdx].current?.focus();
      // Check if all filled
      const code = newOtp.join('');
      if (code.length === 4 && /^\d{4}$/.test(code)) {
        setTimeout(() => handleVerifyOTP(code), 100);
      }
      return;
    }

    // Single digit
    const newOtp = [...otp];
    newOtp[index] = digits.slice(-1);
    setOtp(newOtp);

    if (digits && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
    // Auto-submit when last digit entered
    if (digits && index === 3) {
      const code = [...newOtp.slice(0, 3), digits.slice(-1)].join('');
      if (code.length === 4) {
        handleVerifyOTP(code);
      }
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      const newOtp = pasted.split('');
      setOtp(newOtp);
      otpRefs[3].current?.focus();
      setTimeout(() => handleVerifyOTP(pasted), 100);
    }
  };

  const finishLogin = (data) => {
    if (data.session_token) localStorage.setItem('session_token', data.session_token);
    const userData = data.user;
    if (data.is_new_user) {
      trackSignUp('phone');
    } else {
      trackLogin('phone');
    }
    setUser(userData);
    const returnUrl = searchParams.get('returnUrl') || '/app/dashboard';
    navigate(returnUrl, { replace: true });
  };

  const handleVerifyOTP = async (code) => {
    if (!code || code.length !== 4) return;
    setLoading(true);
    setError('');
    try {
      // Branch based on current flow (registration vs forgot-password)
      if (step === 'forgot-otp') {
        // Forgot-password: don't call verify-otp; just advance to set-new-password.
        // We'll validate OTP together with the new password on /reset-password.
        setPendingToken(code); // stash code for reset-password step
        setStep('forgot-password');
        setLoading(false);
        return;
      }

      const response = await httpClient.post(`${API}/api/auth/phone/verify-otp`, {
        phone_number: fullPhoneNumber,
        code
      });
      const data = response.data || response;

      if (data.needs_password_setup && data.pending_token) {
        // Registration flow: show password setup screen
        setPendingToken(data.pending_token);
        setStep('setup-password');
        setLoading(false);
        return;
      }

      // Legacy branch (existing user with password) — shouldn't happen in new flow, but safe
      finishLogin(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.data?.detail || 'Codice non valido';
      setError(detail);
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e?.preventDefault();
    setError('');
    if (newPassword.length < 8 || !/\d/.test(newPassword)) {
      setError('La password deve avere almeno 8 caratteri e contenere un numero');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('Le password non coincidono');
      return;
    }
    setLoading(true);
    try {
      const response = await httpClient.post(`${API}/api/auth/phone/complete-registration`, {
        pending_token: pendingToken,
        password: (newPassword || '').trim(),
        confirm_password: (newPasswordConfirm || '').trim(),
      });
      const data = response.data || response;
      finishLogin(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.data?.detail || 'Errore nella registrazione';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e?.preventDefault();
    setError('');
    if (!phoneNumber.trim() || phoneNumber.length < 5) {
      setError('Inserisci un numero di telefono valido');
      return;
    }
    if (!loginPassword) {
      setError('Inserisci la password');
      return;
    }
    setLoading(true);
    try {
      const response = await httpClient.post(`${API}/api/auth/phone/login`, {
        phone_number: fullPhoneNumber,
        password: (loginPassword || '').trim(),
      });
      const data = response.data || response;
      finishLogin(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.data?.detail || 'Credenziali non valide';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    setError('');
    if (newPassword.length < 8 || !/\d/.test(newPassword)) {
      setError('La password deve avere almeno 8 caratteri e contenere un numero');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('Le password non coincidono');
      return;
    }
    setLoading(true);
    try {
      const code = pendingToken; // stashed in handleVerifyOTP
      const response = await httpClient.post(`${API}/api/auth/phone/reset-password`, {
        phone_number: fullPhoneNumber,
        code,
        new_password: (newPassword || '').trim(),
      });
      const data = response.data || response;
      finishLogin(data);
    } catch (err) {
      // Surface the real backend error so users don't just see the
      // generic fallback. Several error shapes have been observed in the
      // wild (iOS WKWebView quirks, CORS preflight failures, etc.).
      console.error('[reset-password] error:', err, 'response:', err?.response);
      const detail =
        err?.response?.data?.detail ||
        err?.data?.detail ||
        err?.message ||
        'Errore nel reset della password';
      setError(detail);
      if (typeof detail === 'string' && detail.toLowerCase().includes('codice')) {
        // Back to OTP step on bad code
        setStep('forgot-otp');
        setOtp(['', '', '', '']);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (resendTimer > 0) return;
    setOtp(['', '', '', '']);
    setError('');
    handleSendOTP();
  };

  const filteredCountries = countrySearch
    ? COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.code.includes(countrySearch) ||
        c.iso.toLowerCase().includes(countrySearch.toLowerCase())
      )
    : COUNTRIES;

  return (
    <div className="auth-page">
      <img src={SPLASH_IMAGE} alt="" className="auth-page-bg auth-mobile-only" style={{ filter: 'brightness(0.7)' }} />
      <img src={DESKTOP_BG_IMAGE} alt="" className="auth-page-desktop-bg" style={{ filter: 'brightness(0.7)' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/90 z-[1]" />

      <div className="auth-page-content">
        {/* Language selector badge — top-right of the auth screen. Lets
            users override the auto-detected language (geo/IP) at any time
            BEFORE login. Reuses the global LanguageContext. */}
        <div className="absolute top-[max(env(safe-area-inset-top),16px)] right-4 z-[55]">
          <LanguageSelectorBadge />
        </div>
        <div className="px-5 pb-6 w-full max-w-md mx-auto">
          <div className="backdrop-blur-xl bg-black/25 rounded-3xl p-6 border border-white/10 shadow-2xl">
            {/* Logo */}
            <div className="text-center mb-5">
              <img src={LOGO_LIGHT} alt="Slimmy" className="h-9 mx-auto mb-1.5 drop-shadow-lg" />
              <p className="text-white/90 text-sm font-medium">
                {step === 'login-choice' && (t('auth.howToLogin') || 'Come vuoi accedere?')}
                {step === 'login' && (t('auth.loginWithPhoneTitle') || 'Accedi con telefono')}
                {step === 'register-choice' && (t('auth.howToRegister') || 'Come vuoi registrarti?')}
                {step === 'phone' && (t('auth.registerWithPhoneTitle') || 'Registrati con telefono')}
                {step === 'otp' && (t('auth.enterCode') || 'Inserisci il codice')}
                {step === 'setup-password' && (t('auth.setPasswordTitle') || 'Imposta la tua password')}
                {step === 'forgot-phone' && (t('auth.recoverPasswordTitle') || 'Recupera password')}
                {step === 'forgot-otp' && (t('auth.enterCode') || 'Inserisci il codice')}
                {step === 'forgot-password' && (t('auth.setNewPasswordTitle') || 'Imposta nuova password')}
              </p>
            </div>

            {/* ────── LOGIN CHOICE: email or phone (mirrors register-choice) ────── */}
            {step === 'login-choice' && (
              <div className="space-y-3">
                <p className="text-white/70 text-xs text-center mb-2">
                  {t('auth.chooseLoginMethod') || 'Scegli come accedere'}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/app/accedi')}
                  className="w-full py-3.5 rounded-2xl font-semibold bg-[#E8334A] text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                  data-testid="login-with-email-btn"
                >
                  <Mail size={18} />
                  {t('auth.loginWithEmail') || 'Accedi con email'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('login'); setError(''); }}
                  className="w-full py-3.5 rounded-2xl font-semibold bg-white/10 text-white border border-white/20 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                  data-testid="login-with-phone-btn"
                >
                  <Phone size={18} />
                  {t('auth.loginWithPhone') || 'Accedi con numero'}
                </button>
                {googleLoginEnabled && (
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="w-full py-3.5 rounded-2xl font-semibold bg-white text-gray-800 flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
                    data-testid="login-with-google-btn"
                  >
                    <GoogleIcon />
                    {t('auth.googleLogin') || 'Accedi con Google'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setStep('register-choice'); setError(''); }}
                  className="w-full text-center text-white/60 text-sm hover:text-white/80 pt-2"
                  data-testid="go-to-register-from-choice"
                >
                  {t('auth.firstAccessRegister') || 'Primo accesso?'} <span className="text-[#E8334A] font-medium">{t('auth.signUpNow') || 'Registrati'}</span>
                </button>
              </div>
            )}

            {/* ────── LOGIN: phone + password ────── */}
            {step === 'login' && (
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCountryPicker(true)}
                    className="flex items-center gap-1.5 px-3 h-12 rounded-2xl bg-white/10 border border-white/20 text-white shrink-0"
                    data-testid="country-picker-btn"
                  >
                    <span className="text-lg">{country.flag}</span>
                    <span className="text-sm font-medium">{country.code}</span>
                    <ChevronDown size={14} className="text-white/50" />
                  </button>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/70" size={18} />
                    <input
                      ref={phoneInputRef}
                      type="tel"
                      inputMode="numeric"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d]/g, ''))}
                      placeholder={t('auth.phoneNumberPlaceholder') || 'Numero di telefono'}
                      className="w-full pl-11 pr-4 h-12 rounded-2xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent"
                      data-testid="phone-input"
                      autoFocus
                    />
                  </div>
                </div>

                <PasswordInput
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder={t('auth.password') || 'Password'}
                  className="w-full px-4 h-12 rounded-2xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent"
                  data-testid="login-password-input"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />

                {error && (
                  <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!phoneNumber || phoneNumber.length < 5 || !loginPassword || loading}
                  className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                    phoneNumber && phoneNumber.length >= 5 && loginPassword && !loading
                      ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                      : 'bg-white/20 text-white/50 cursor-not-allowed'
                  }`}
                  data-testid="phone-login-btn"
                >
                  {loading ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    t('auth.login') || 'Accedi'
                  )}
                </button>

                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => { setStep('forgot-phone'); setError(''); setLoginPassword(''); }}
                    className="text-white/70 text-sm hover:text-white"
                    data-testid="forgot-password-link"
                  >
                    {t('auth.forgotPassword') || 'Password dimenticata?'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStep('register-choice'); setError(''); setLoginPassword(''); }}
                    className="text-[#E8334A] text-sm font-medium hover:underline"
                    data-testid="go-to-register-link"
                  >
                    {t('auth.firstAccessRegister') || 'Primo accesso?'} {t('auth.signUpNow') || 'Registrati'}
                  </button>
                </div>

                <div className="pt-2 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => { setStep('login-choice'); setError(''); setLoginPassword(''); }}
                    className="w-full text-center text-white/50 text-sm hover:text-white/70 flex items-center justify-center gap-1.5"
                    data-testid="back-to-login-choice"
                  >
                    ← {t('auth.backToLogin') || 'Torna al login'}
                  </button>
                </div>
              </form>
            )}

            {/* ────── REGISTER CHOICE: email or phone ────── */}
            {step === 'register-choice' && (
              <div className="space-y-3">
                <p className="text-white/70 text-xs text-center mb-2">
                  {t('auth.chooseRegisterMethod') || 'Scegli come creare il tuo account'}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/app/registrati')}
                  className="w-full py-3.5 rounded-2xl font-semibold bg-[#E8334A] text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                  data-testid="register-with-email-btn"
                >
                  <Mail size={18} />
                  {t('auth.registerWithEmail') || 'Registrati con email'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('phone'); setError(''); }}
                  className="w-full py-3.5 rounded-2xl font-semibold bg-white/10 text-white border border-white/20 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                  data-testid="register-with-phone-btn"
                >
                  <Phone size={18} />
                  {t('auth.registerWithPhone') || 'Registrati con numero'}
                </button>
                {googleLoginEnabled && (
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="w-full py-3.5 rounded-2xl font-semibold bg-white text-gray-800 flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
                    data-testid="register-with-google-btn"
                  >
                    <GoogleIcon />
                    {t('auth.continueWithGoogle') || 'Continua con Google'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setStep('login-choice'); setError(''); }}
                  className="w-full text-center text-white/60 text-sm hover:text-white/80 pt-2"
                  data-testid="back-to-login-btn-rc"
                >
                  ← {t('auth.backToLogin') || 'Torna al login'}
                </button>

                {/* Legal agreement — required for App Store / GDPR compliance.
                    Links open the existing /terms and /privacy static pages
                    via react-router navigate (works in both web + WKWebView). */}
                <p className="text-white/55 text-xs text-center leading-relaxed pt-3" data-testid="signup-agreement-text">
                  {t('auth.signupAgreementText') || 'Registrandoti accetti i'}{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/terms')}
                    className="text-[#E8334A] underline underline-offset-2 hover:text-[#ff5a6f]"
                    data-testid="signup-agreement-terms-link"
                  >
                    {t('auth.signupAgreementTerms') || 'Termini di servizio'}
                  </button>
                  <br />
                  {t('auth.signupAgreementAnd') || 'e la'}{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/privacy')}
                    className="text-[#E8334A] underline underline-offset-2 hover:text-[#ff5a6f]"
                    data-testid="signup-agreement-privacy-link"
                  >
                    {t('auth.signupAgreementPrivacy') || 'Privacy Policy'}
                  </button>
                  .
                </p>
              </div>
            )}

            {/* ────── REGISTER: phone input (sends OTP) ────── */}
            {(step === 'phone' || step === 'forgot-phone') && (
              <form onSubmit={handleSendOTP} className="space-y-4">
                <p className="text-white/70 text-xs text-center">
                  {step === 'forgot-phone'
                    ? 'Ti invieremo un codice SMS per reimpostare la password.'
                    : 'Ti invieremo un codice SMS per verificare il numero.'}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCountryPicker(true)}
                    className="flex items-center gap-1.5 px-3 h-12 rounded-2xl bg-white/10 border border-white/20 text-white shrink-0"
                    data-testid="country-picker-btn-reg"
                  >
                    <span className="text-lg">{country.flag}</span>
                    <span className="text-sm font-medium">{country.code}</span>
                    <ChevronDown size={14} className="text-white/50" />
                  </button>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/70" size={18} />
                    <input
                      ref={phoneInputRef}
                      type="tel"
                      inputMode="numeric"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d]/g, ''))}
                      placeholder={t('auth.phoneNumberPlaceholder') || 'Numero di telefono'}
                      className="w-full pl-11 pr-4 h-12 rounded-2xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent"
                      data-testid="phone-input-reg"
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!phoneNumber || phoneNumber.length < 5 || loading}
                  className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                    phoneNumber && phoneNumber.length >= 5 && !loading
                      ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                      : 'bg-white/20 text-white/50 cursor-not-allowed'
                  }`}
                  data-testid="send-otp-btn"
                >
                  {loading ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    t('auth.sendSmsCode') || 'Invia codice SMS'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('login-choice'); setError(''); setOtp(['', '', '', '']); }}
                  className="w-full text-center text-white/60 text-sm hover:text-white/80"
                  data-testid="back-to-login-btn"
                >
                  ← {t('auth.backToLogin') || 'Torna al login'}
                </button>
              </form>
            )}

            {/* ────── SETUP PASSWORD after OTP (registration) ────── */}
            {(step === 'setup-password' || step === 'forgot-password') && (
              <form
                onSubmit={step === 'setup-password' ? handleCompleteRegistration : handleResetPassword}
                className="space-y-4"
              >
                <p className="text-white/70 text-xs text-center">
                  {step === 'setup-password'
                    ? (t('auth.chooseSecurePassword') || 'Scegli una password sicura per il tuo account. La userai per accedere senza SMS.')
                    : (t('auth.enterNewPassword') || 'Inserisci la nuova password per il tuo account.')}
                </p>
                <PasswordInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('auth.newPasswordPlaceholder') || 'Nuova password (min 8 caratteri, 1 numero)'}
                  className="w-full px-4 h-12 rounded-2xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent"
                  data-testid="new-password-input"
                  autoComplete="new-password"
                  autoFocus
                />
                <PasswordInput
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  placeholder={t('auth.confirmPasswordPlaceholder') || 'Conferma password'}
                  className="w-full px-4 h-12 rounded-2xl bg-white/10 border border-white/20 text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent"
                  data-testid="new-password-confirm-input"
                  autoComplete="new-password"
                />
                {error && (
                  <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={!newPassword || !newPasswordConfirm || loading}
                  className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                    newPassword && newPasswordConfirm && !loading
                      ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                      : 'bg-white/20 text-white/50 cursor-not-allowed'
                  }`}
                  data-testid="complete-setup-btn"
                >
                  {loading ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    step === 'setup-password'
                      ? (t('auth.completeRegistrationBtn') || 'Completa registrazione')
                      : (t('auth.saveNewPassword') || 'Salva nuova password')
                  )}
                </button>
              </form>
            )}

            {/* ────── OTP verification step (registration + forgot) ────── */}
            {(step === 'otp' || step === 'forgot-otp') && (
              <div className="space-y-4">
                <p className="text-white/70 text-sm text-center">
                  {t('auth.codeSentTo') || 'Codice inviato a'} <span className="text-white font-medium">{country.flag} {fullPhoneNumber}</span>
                </p>

                {/* OTP input boxes */}
                <div className="flex justify-center gap-3" onPaste={handleOTPPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={`otp-${i}`}
                      ref={otpRefs[i]}
                      type="tel"
                      inputMode="numeric"
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      value={digit}
                      onChange={(e) => handleOTPChange(i, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(i, e)}
                      className="w-14 h-14 text-center text-2xl font-bold rounded-2xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent"
                      data-testid={`otp-input-${i}`}
                    />
                  ))}
                </div>

                {error && (
                  <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                    {error}
                  </div>
                )}

                {/* Resend */}
                <div className="text-center">
                  {resendTimer > 0 ? (
                    <p className="text-white/50 text-sm">Rinvia codice tra {resendTimer}s</p>
                  ) : (
                    <button
                      onClick={handleResend}
                      className="text-[#E8334A] text-sm font-medium hover:underline"
                      data-testid="resend-otp-btn"
                    >
                      Rinvia codice
                    </button>
                  )}
                </div>

                {/* Change number */}
                <button
                  onClick={() => { setStep(step === 'forgot-otp' ? 'forgot-phone' : 'phone'); setOtp(['', '', '', '']); setError(''); }}
                  className="w-full text-center text-white/50 text-sm hover:text-white/70"
                  data-testid="change-number-btn"
                >
                  Cambia numero
                </button>

                {loading && (
                  <div className="flex justify-center">
                    <div className="animate-spin w-6 h-6 border-2 border-[#E8334A] border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Version badge */}
      <span 
        className="fixed bottom-2 left-1/2 -translate-x-1/2 text-white/30 text-[10px] z-[2] pt-4 select-none"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >{APP_VERSION}</span>

      {/* Hidden dev tap area - top left, fully invisible */}
      <div 
        style={{ position: 'fixed', width: '40px', height: '40px', top: 'calc(env(safe-area-inset-top, 0px) + 20px)', left: '20px', WebkitTapHighlightColor: 'transparent', zIndex: 9999, background: 'transparent' }}
        onTouchEnd={(e) => { e.preventDefault(); handleDevTap(); }}
        onClick={handleDevTap}
      />

      {/* Dev switch modal */}
      {showDevSwitch && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center px-6" onClick={() => setShowDevSwitch(false)}>
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-xs" onClick={e => e.stopPropagation()}>
            <p className="text-white text-sm text-center mb-4">
              {API_URL.includes('preview') ? 'Preview → Produzione' : 'Produzione → Preview'}
            </p>
            <input
              type="password"
              value={devPassword}
              onChange={e => setDevPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white text-center mb-4 focus:outline-none"
              autoFocus
            />
            <div className="flex gap-3">
              <button onClick={() => { setShowDevSwitch(false); setDevPassword(''); }} className="flex-1 py-2.5 rounded-xl bg-white/10 text-white/70 text-sm">Annulla</button>
              <button onClick={handleDevSwitch} className="flex-1 py-2.5 rounded-xl bg-[#E8334A] text-white text-sm font-medium">Switch</button>
            </div>
          </div>
        </div>
      )}

      {/* Country picker modal */}
      {showCountryPicker && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-end sm:items-center justify-center" onClick={() => setShowCountryPicker(false)}>
          <div
            className="bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm max-h-[70vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10">
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Cerca paese..."
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none"
                autoFocus
                data-testid="country-search-input"
              />
            </div>
            <div className="overflow-y-auto flex-1 pb-safe">
              {filteredCountries.map(c => (
                <button
                  key={c.iso}
                  onClick={() => { setCountry(c); setShowCountryPicker(false); setCountrySearch(''); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${c.iso === country.iso ? 'bg-[#E8334A]/20' : ''}`}
                  data-testid={`country-${c.iso}`}
                >
                  <span className="text-2xl">{c.flag}</span>
                  <span className="text-white text-sm flex-1 text-left">{c.name}</span>
                  <span className="text-white/50 text-sm font-mono">{c.code}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
